import { z } from "zod";

// The member's own Claude or Codex login (ADR-023 §7, S27.06). A member signs in once from the
// browser: for Claude, the PKCE paste-back that Claude Code's own sign-in uses (open Claude's
// page, approve, paste back `code#state`); for Codex, OpenAI's device code (open OpenAI's page,
// type the code, approve) while the server polls. The server exchanges what it is given and keeps
// only the login: Claude's long-lived inference token, or the `auth.json` Codex reads, refresh
// token included. Each is a vault secret of the kind `harness-login`, owned by the member, one per
// harness: never listed with the organization's secrets, never answered by the egress proxy's
// door, released only into a turn of the member's own agent on that harness.

/** The harnesses that run on a member's own login; our model needs none (ADR-023 §7). */
export const HARNESS_LOGIN_HARNESSES = ["claude-code", "codex"] as const;
export const harnessLoginHarnessSchema = z.enum(HARNESS_LOGIN_HARNESSES);
export type HarnessLoginHarness = z.infer<typeof harnessLoginHarnessSchema>;

/** What anyone may see about a member's login: never the token, never the file. */
export const harnessLoginSchema = z.object({
  harness: harnessLoginHarnessSchema,
  /** The account the provider named (an email), when it named one. */
  accountLabel: z.string().nullable(),
  /** When the member last signed in. */
  signedInAt: z.string(),
  /** When a turn last sent Codex's refreshed `auth.json` back; null until one did. */
  refreshedAt: z.string().nullable(),
  /** When the provider said the login stops working (Claude's year); null when it did not say. */
  expiresAt: z.string().nullable(),
});
export type HarnessLogin = z.infer<typeof harnessLoginSchema>;

export const harnessLoginsInputSchema = z.object({});
export const harnessLoginsOutputSchema = z.object({ logins: z.array(harnessLoginSchema) });
export type HarnessLogins = z.infer<typeof harnessLoginsOutputSchema>;

export const startHarnessSignInInputSchema = z.object({ harness: harnessLoginHarnessSchema });
export type StartHarnessSignInInput = z.infer<typeof startHarnessSignInInputSchema>;

/** A sign-in the server started. The browser holds only its id and what the member must see:
 * the verifier, the state and the device's own id stay on the server. */
export const harnessSignInSchema = z.discriminatedUnion("harness", [
  z.object({
    harness: z.literal("claude-code"),
    signInId: z.string(),
    expiresAt: z.string(),
    /** Claude's sign-in page, for Claude Code's own client, asking only for inference. */
    authorizeUrl: z.string(),
  }),
  z.object({
    harness: z.literal("codex"),
    signInId: z.string(),
    expiresAt: z.string(),
    /** The one-time code the member types on OpenAI's page. */
    userCode: z.string(),
    /** OpenAI's device page. */
    verificationUrl: z.string(),
    /** How often the browser asks the server to check; the server polls OpenAI no faster. */
    intervalSeconds: z.number().int().min(1),
  }),
]);
export type HarnessSignIn = z.infer<typeof harnessSignInSchema>;

const signInIdSchema = z.string().min(1).max(64);

/** What Claude's page shows: `code#state`, pasted back whole (the code alone is taken too). */
export const finishClaudeSignInInputSchema = z.object({
  signInId: signInIdSchema,
  code: z.string().trim().min(1).max(4096),
});
export type FinishClaudeSignInInput = z.infer<typeof finishClaudeSignInInputSchema>;

export const checkCodexSignInInputSchema = z.object({ signInId: signInIdSchema });
export type CheckCodexSignInInput = z.infer<typeof checkCodexSignInInputSchema>;

/** One check of a Codex sign-in: still waiting for the member to approve, or signed in. */
export const codexSignInCheckSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("waiting"), intervalSeconds: z.number().int().min(1) }),
  z.object({ status: z.literal("signed_in"), login: harnessLoginSchema }),
]);
export type CodexSignInCheck = z.infer<typeof codexSignInCheckSchema>;

export const removeHarnessLoginInputSchema = z.object({ harness: harnessLoginHarnessSchema });
export type RemoveHarnessLoginInput = z.infer<typeof removeHarnessLoginInputSchema>;

/**
 * The login one turn runs on (seam 6), as `harnessLoginForTurn` answers it. The runtime puts it
 * in the `deliver` frame's `login` field: Claude's `env.CLAUDE_CODE_OAUTH_TOKEN`, or Codex's
 * `authJson`, which the supervisor writes as `$CODEX_HOME/auth.json` (D574).
 */
export const harnessTurnLoginSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("claude"),
    env: z.object({ CLAUDE_CODE_OAUTH_TOKEN: z.string().min(1) }),
  }),
  z.object({ kind: z.literal("codex"), authJson: z.string().min(1) }),
]);
export type HarnessTurnLogin = z.infer<typeof harnessTurnLoginSchema>;

/** The largest `auth.json` taken back, in characters: the deliver frame's own limit (seam 6). */
export const MAX_AUTH_JSON_CHARS = 64 * 1024;

/** The `auth.json` Codex reads and refreshes in place: its tokens, the refresh token included. */
export const codexAuthJsonSchema = z.looseObject({
  tokens: z.looseObject({
    id_token: z.string().min(1),
    access_token: z.string().min(1),
    refresh_token: z.string().min(1),
    account_id: z.string().optional(),
  }),
  last_refresh: z.string().optional(),
});
export type CodexAuthJson = z.infer<typeof codexAuthJsonSchema>;

/** `PUT /api/agents/me/codex-login`'s body: the file as the turn left it. The turn's token rides
 * in `X-Zaistev-Turn`, which the host relay adds. */
export const codexLoginWriteBackInputSchema = z.object({
  body: z.object({ authJson: z.string().min(2).max(MAX_AUTH_JSON_CHARS) }).strict(),
});
